import React, { useEffect, useState } from "react";
import { useToast } from "../utils/useToast";
import GlobalToast from "../components/GlobalToast.jsx";
import apiWithRefresh from "../utils/apiWithRefresh";
import * as XLSX from "xlsx";
import {
  Box,
  Typography,
  Alert,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  InputAdornment,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
//import PaymentIcon from "@mui/icons-material/Payment";
import { Helmet } from "react-helmet-async";
import assessmentStatusEnum from "../utils/assessmentstatusEnum.js";
import feePaymentEnum from "../utils/feePaymentEnum.js";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

const AdminEnrollments = () => {
  // Bulk upload state
  const [bulkUploadDialog, setBulkUploadDialog] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadResult, setBulkUploadResult] = useState([]);

  // // Handler to trigger first month payment order (subscription)
  // const handleTriggerFirstMonthPayment = async (enrollmentId) => {
  //   try {
  //     await apiWithRefresh.post(
  //       `learner-enrollments/${enrollmentId}/trigger-first-month-payment`,
  //     );
  //     showToast("First month payment order triggered!", "success");
  //     fetchEnrollments();
  //   } catch (err) {
  //     let errorMsg =
  //       err && (err.response?.data?.message || err.message || err.toString());
  //     setError(errorMsg);
  //     showToast(errorMsg || "Failed to trigger payment order!", "error");
  //   }
  // };

  // Open/close bulk upload dialog
  const openBulkUploadDialog = () => setBulkUploadDialog(true);
  const closeBulkUploadDialog = () => {
    setBulkUploadDialog(false);
    setBulkFile(null);
    setBulkUploadResult([]);
  };

  // Handle file input
  const handleBulkFileChange = (e) => {
    setBulkFile(e.target.files[0] || null);
  };

  const [enrollments, setEnrollments] = useState([]);

  // Bulk upload handler
  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    setBulkUploading(true);
    setBulkUploadResult([]);
    try {
      const data = await bulkFile.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets["CashEnrollment"];
      if (!sheet) throw new Error("Sheet 'CashEnrollment' not found");
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      // Find header row
      const header = rows[0];
      const idx = {
        learnerName: header.indexOf("learnerName"),
        email: header.indexOf("email"),
        phone: header.indexOf("phone"),
        countryId: header.indexOf("countryId"),
        fatherName: header.indexOf("fatherName"),
        address: header.indexOf("address"),
        batchId: header.indexOf("batchId"),
        preferredLevelId: header.indexOf("preferredLevelId"),
        feePlanId: header.indexOf("feePlanId"),
      };
      if (Object.values(idx).some((i) => i === -1)) {
        throw new Error("Missing required columns in sheet header");
      }
      // Prepare payloads
      const payloads = rows.slice(1).map((row) => ({
        learnerName: row[idx.learnerName],
        email: row[idx.email],
        phone: row[idx.phone],
        countryCurrencyId: row[idx.countryId] || 1,
        batchId: row[idx.batchId] || 1,
        fatherName: row[idx.fatherName],
        address: row[idx.address] || "",
        assessmentStatus: assessmentStatusEnum.PASS,
        admissionFeePaid: true,
        isCashEnrollment: true,
        batchAllocated: true,
        consentAccepted: true,
        preferredLevelId: row[idx.preferredLevelId] || 1, // Default level for cash enrollments
        feePlanId: row[idx.feePlanId] || 1, // Default fee plan for cash enrollments
        configAppId: 4, // Default app config for cash enrollments
      }));
      console.log("Prepared payloads for bulk upload:", payloads);
      // Validate email/phone uniqueness and skip invalid
      const results = [];
      const skipped = [];
      for (const payload of payloads) {
        // Validate email
        let emailUnique = true;
        let phoneUnique = true;
        let emailError = "";
        let phoneError = "";
        try {
          const emailRes = await apiWithRefresh.post(
            "checkunique/learner-email",
            { email: payload.email },
          );
          if (emailRes.exists) {
            emailUnique = false;
            emailError = "Email already exists";
          }
        } catch (err) {
          emailUnique = false;
          emailError = "Email check failed";
        }
        try {
          const phoneRes = await apiWithRefresh.post(
            "checkunique/learner-phone",
            { phone: payload.phone },
          );
          if (phoneRes.exists) {
            phoneUnique = false;
            phoneError = "Phone already exists";
          }
        } catch (err) {
          phoneUnique = false;
          phoneError = "Phone check failed";
        }
        if (!emailUnique || !phoneUnique) {
          skipped.push({
            ...payload,
            status: "skipped",
            error: `${emailError} ${phoneError}`.trim(),
          });
          continue;
        }
        // POST valid payload
        try {
          await apiWithRefresh.post("learner-enrollments", payload);
          results.push({ ...payload, status: "success" });
        } catch (err) {
          results.push({ ...payload, status: "error", error: err.message });
        }
      }
      setBulkUploadResult([...results, ...skipped]);
      const skippedEmails = skipped.map((s) => s.email).join(", ");
      showToast(
        `Bulk upload complete: ${results.filter((r) => r.status === "success").length} success, ${results.filter((r) => r.status === "error").length} error, ${skipped.length} skipped. Skipped emails: ${skippedEmails}`,
        "success",
      );
      fetchEnrollments();
    } catch (err) {
      setBulkUploadResult([{ status: "error", error: err.message }]);
      showToast(err.message || "Bulk upload failed", "error");
    } finally {
      setBulkUploading(false);
    }
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, showToast] = useToast();
  const [search, setSearch] = useState("");
  const [filterAssessment, setFilterAssessment] = useState("");
  const [filterPayment, setFilterPayment] = useState("");
  const [commentDialog, setCommentDialog] = useState({
    open: false,
    id: null,
    status: null,
    comment: "",
    loading: false,
  });
  const [detailDialog, setDetailDialog] = useState({
    open: false,
    enrollment: null,
  });

  useEffect(() => {
    fetchEnrollments();
  }, []);

  const fetchEnrollments = () => {
    setLoading(true);
    apiWithRefresh
      .get("learner-enrollments")
      .then((data) => {
        setEnrollments(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  };

  // Handler for updating assessment/payment status
  const handleUpdate = async (id, updateData) => {
    try {
      await apiWithRefresh.patch(
        `learner-enrollments/${id}/assessment`,
        updateData,
      );
      fetchEnrollments();
      showToast("Update successful!", "success");
    } catch (err) {
      let errorMsg =
        err && (err.response?.data?.message || err.message || err.toString());
      setError(errorMsg);
      showToast(errorMsg || "Update failed!", "error");
    }
  };

  // Open dialog for assessment comments
  const openCommentDialog = (id, status, currentComment = "") => {
    setCommentDialog({
      open: true,
      id,
      status,
      comment: currentComment,
      loading: false,
    });
  };
  const closeCommentDialog = () =>
    setCommentDialog({ ...commentDialog, open: false });
  const submitCommentDialog = async () => {
    setCommentDialog((d) => ({ ...d, loading: true }));
    await handleUpdate(commentDialog.id, {
      assessmentStatus: commentDialog.status,
      assessmentComments: commentDialog.comment,
    });
    setCommentDialog({
      open: false,
      id: null,
      status: null,
      comment: "",
      loading: false,
    });
  };

  // Open dialog for details
  const openDetailDialog = (enrollment) =>
    setDetailDialog({ open: true, enrollment });
  const closeDetailDialog = () =>
    setDetailDialog({ open: false, enrollment: null });

  // Filtering and search logic
  const filteredRows = enrollments
    .filter((en) => {
      if (
        search &&
        !(
          (en.learnerName || "").toLowerCase().includes(search.toLowerCase()) ||
          (en.email || "").toLowerCase().includes(search.toLowerCase())
        )
      )
        return false;
      if (filterAssessment && en.assessmentStatus !== filterAssessment)
        return false;
      if (filterPayment && !en[filterPayment]) return false;
      return true;
    })
    .map((en) => ({
      ...en,
      id: en.id,
      userName: en.learnerName || en.userId?.name || "",
      levelName: en.preferredLevel?.name || "",
      monthlyFeePaid: en.monthlyFeePaid
        ? en.feePlanId?.name || String(feePaymentEnum.PAID).toUpperCase()
        : String(feePaymentEnum.PENDING).toUpperCase(),
      admissionFeePaid: en.admissionFeePaid
        ? String(feePaymentEnum.PAID).toUpperCase()
        : String(feePaymentEnum.PENDING).toUpperCase(),
      batchAllocated: en.batchAllocated ? en.batchId?.name || "Allocated" : "-",
      specialChanceFeePaid: en.specialChanceFeePaid
        ? String(feePaymentEnum.PAID).toUpperCase()
        : en.assessmentStatus
          ? "N/A"
          : String(feePaymentEnum.PENDING).toUpperCase(),
      assessmentStatus: en.assessmentStatus
        ? String(en.assessmentStatus).toUpperCase()
        : "",
      assessmentComments: en.assessmentComments || "",
      isQrEnrollment: en.isQrEnrollment ? "Yes" : "No",
      isCashEnrollment: en.isCashEnrollment ? "Yes" : "No",
      modifyOn: en.updatedAt ? new Date(en.updatedAt).toLocaleDateString() : "",
    }));

  return (
    <React.Fragment>
      <Helmet>
        <title>AKAAL KIDS - Admin Panel | Enrollments</title>
      </Helmet>
      <Box
        sx={{
          mt: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          margin: "0 auto",
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
          Manage Enrollments
        </Typography>
        {/* Bulk Upload Button */}

        <Stack direction="row" spacing={2} sx={{ mb: 2, width: "100%" }}>
          <TextField
            size="small"
            placeholder="Search by name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220 }}
          />
          <TextField
            select
            size="small"
            label="Assessment"
            value={filterAssessment}
            onChange={(e) => setFilterAssessment(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All</MenuItem>
            {Object.values(assessmentStatusEnum).map((v) => (
              <MenuItem key={v} value={v}>
                {v.toUpperCase()}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Payment"
            value={filterPayment}
            onChange={(e) => setFilterPayment(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="admissionFeePaid">Admission</MenuItem>
            <MenuItem value="monthlyFeePaid">Monthly</MenuItem>
            <MenuItem value="specialChanceFeePaid">Special Chance</MenuItem>
            <MenuItem value="isQrEnrollment">QR Enrollment</MenuItem>
            <MenuItem value="isCashEnrollment">Cash Enrollment</MenuItem>
          </TextField>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={openBulkUploadDialog}
            >
              Bulk Upload Offline Enrollments
            </Button>
          </Box>
        </Stack>
        {loading && <Typography>Loading...</Typography>}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && (
          <Box
            sx={{
              width: "100%",
              border: "2px solid #110f0fff",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <DataGrid
              autoHeight
              virtualizeColumnsWithAutoRowHeight={true}
              rows={filteredRows}
              columns={[
                { field: "userName", headerName: "Learner", flex: 1 },
                { field: "email", headerName: "Email", flex: 1 },
                {
                  field: "assessmentStatus",
                  headerName: "Assessment",
                  flex: 1,
                },
                {
                  field: "specialChanceFeePaid",
                  headerName: "Spl. Chance Fee",
                  flex: 1,
                },
                {
                  field: "admissionFeePaid",
                  headerName: "Adm. Fee",
                  flex: 1,
                },
                {
                  field: "monthlyFeePaid",
                  headerName: "Monthly Fee",
                  flex: 1,
                },
                {
                  field: "isQrEnrollment",
                  headerName: "QR Enrollment",
                  flex: 1,
                },
                {
                  field: "isCashEnrollment",
                  headerName: "Cash Enrollment",
                  flex: 1,
                },
                {
                  field: "batchAllocated",
                  headerName: "Batch",
                  flex: 1,
                },
                { field: "levelName", headerName: "Level", flex: 1 },
                {
                  field: "modifyOn",
                  headerName: "Modified",
                  flex: 1,
                },
                {
                  field: "actions",
                  headerName: "Actions",
                  sortable: false,
                  renderCell: (params) => (
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        size="small"
                        color="info"
                        onClick={() => openDetailDialog(params.row)}
                        title="View Details"
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                      {/* Cash Enrollment: Resend First Month Payment Link */}
                      {params.row.isCashEnrollment === "Yes" &&
                        !params.row.monthlyFeePaid && (
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            disabled={loading}
                            onClick={async () => {
                              try {
                                await apiWithRefresh.post(
                                  `learner-enrollments/${params.row.id}/resend-first-month-payment-link`,
                                );
                                showToast(
                                  "First month payment link resent!",
                                  "success",
                                );
                              } catch (err) {
                                showToast(
                                  err.message ||
                                    "Failed to resend payment link",
                                  "error",
                                );
                              }
                            }}
                          >
                            Resend First Month Payment Link
                          </Button>
                        )}
                      {/* Existing actions for admissionFeePaid/assessmentStatus */}
                      {params.row.admissionFeePaid ===
                        String(feePaymentEnum.PAID).toUpperCase() && (
                        <>
                          {params.row.assessmentStatus !==
                            String(assessmentStatusEnum.PASS).toUpperCase() && (
                            <>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                title="Mark as Pass"
                                startIcon={<CheckCircleIcon />}
                                disabled={loading}
                                onClick={() =>
                                  openCommentDialog(
                                    params.row.id,
                                    assessmentStatusEnum.PASS,
                                    params.row.assessmentComments,
                                  )
                                }
                              ></Button>
                              {params.row.assessmentStatus !==
                                String(
                                  assessmentStatusEnum.FAIL,
                                ).toUpperCase() && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="warning"
                                  disabled={loading}
                                  title="Mark as Fail"
                                  startIcon={<CancelIcon />}
                                  onClick={() =>
                                    openCommentDialog(
                                      params.row.id,
                                      assessmentStatusEnum.FAIL,
                                      params.row.assessmentComments,
                                    )
                                  }
                                ></Button>
                              )}
                            </>
                          )}
                          {/* {params.row.assessmentStatus === String(assessmentStatusEnum.FAIL).toUpperCase() && (
                            <Button
                              size="small"
                              variant="contained"
                              color="info"
                              title="Mark as Special Chance"
                              disabled={loading}
                              startIcon={<StarIcon />}
                              onClick={() =>
                                openCommentDialog(
                                  params.row.id,
                                  assessmentStatusEnum.SPECIAL_CHANCE,
                                  params.row.assessmentComments,
                                )
                              }
                            ></Button>
                          )} */}
                        </>
                      )} 
                    </Stack>
                  ),
                  flex: 1,
                  minWidth: 250,
                },
              ]}
              pageSize={10}
              rowsPerPageOptions={[10, 20, 50]}
              disableSelectionOnClick
            />
          </Box>
        )}
        <GlobalToast toast={toast} />
      </Box>

      {/* Bulk Upload Dialog */}
      <Dialog
        open={bulkUploadDialog}
        onClose={closeBulkUploadDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Upload Offline Enrollments</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Upload an Excel file (.xlsx) with a sheet named{" "}
            <b>CashEnrollment</b> and columns: learnerName, email, phone,
            countryId.
          </Typography>
          <input type="file" accept=".xlsx" onChange={handleBulkFileChange} />
          {bulkUploading && (
            <Box sx={{ mt: 2 }}>
              <CircularProgress />
            </Box>
          )}
          {bulkUploadResult.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1">Upload Results:</Typography>
              <ul>
                {bulkUploadResult.map((r, i) => (
                  <li
                    key={i}
                    style={{ color: r.status === "success" ? "green" : "red" }}
                  >
                    {r.learnerName} ({r.email}): {r.status}
                    {r.error && <span> - {r.error}</span>}
                  </li>
                ))}
              </ul>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBulkUploadDialog} disabled={bulkUploading}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkUpload}
            disabled={!bulkFile || bulkUploading}
            variant="contained"
            color="primary"
          >
            {bulkUploading ? <CircularProgress size={20} /> : "Upload & Import"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assessment Comments Dialog */}
      <Dialog
        open={commentDialog.open}
        onClose={closeCommentDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Assessment Comments</DialogTitle>
        <DialogContent>
          <TextField
            label="Comments"
            value={commentDialog.comment}
            onChange={(e) =>
              setCommentDialog({ ...commentDialog, comment: e.target.value })
            }
            fullWidth
            multiline
            minRows={2}
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCommentDialog} disabled={commentDialog.loading}>
            Cancel
          </Button>
          <Button
            onClick={submitCommentDialog}
            disabled={commentDialog.loading}
            variant="contained"
          >
            {commentDialog.loading ? <CircularProgress size={20} /> : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detailed View Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={closeDetailDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enrollment Details</DialogTitle>
        <DialogContent dividers>
          {detailDialog.enrollment && (
            <Box>
              <Typography variant="subtitle1">
                <b>Learner:</b> {detailDialog.enrollment.learnerName}
              </Typography>
              <Typography variant="subtitle1">
                <b>Email:</b> {detailDialog.enrollment.email}
              </Typography>
              <Typography variant="subtitle1">
                <b>Phone:</b> {detailDialog.enrollment.phone}
              </Typography>
              <Typography variant="subtitle1">
                <b>Level:</b> {detailDialog.enrollment.preferredLevel?.name}
              </Typography>
              <Typography variant="subtitle1">
                <b>Assessment:</b> {detailDialog.enrollment.assessmentStatus}
              </Typography>
              <Typography variant="subtitle1">
                <b>Comments:</b> {detailDialog.enrollment.assessmentComments}
              </Typography>
              <Typography variant="subtitle1">
                <b>Admission Fee Paid:</b>{" "}
                {detailDialog.enrollment.admissionFeePaid}
              </Typography>
              <Typography variant="subtitle1">
                <b>Monthly Fee Paid:</b>{" "}
                {detailDialog.enrollment.monthlyFeePaid}
              </Typography>
              <Typography variant="subtitle1">
                <b>Special Chance Fee Paid:</b>{" "}
                {detailDialog.enrollment.specialChanceFeePaid}
              </Typography>
              <Typography variant="subtitle1">
                <b>Batch:</b> {detailDialog.enrollment.batchId?.name || "-"}
              </Typography>
              <Typography variant="subtitle1">
                <b>Account Activated:</b>{" "}
                {detailDialog.enrollment.accountActivated ? "Yes" : "No"}
              </Typography>
              <Typography variant="subtitle1">
                <b>Last Modified:</b>{" "}
                {detailDialog.enrollment.updatedAt
                  ? new Date(detailDialog.enrollment.updatedAt).toLocaleString()
                  : "-"}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetailDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default AdminEnrollments;
